import { connectHits } from "instantsearch.js/es/connectors/index.js";
import { trackConversion, trackItemClick } from "../algolia-search.js";
import { columnState } from "../components/column-state.js";

let unsubscribeColumnState = null;

let prevProjectListState = [];
let isProjectListFallbackBound = false;
let activeProjectListTrigger = null;

const RECENT_PROJECT_LIST_ROOT_SELECTOR = "#chamfr-recently-added-products";

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const getMultiListSettings = () => algolia.multi_list_button || {};

const getMultiListPopupId = () =>
  getMultiListSettings().popup_id || "add_to_list_popup";

const getProjectListJQuery = () => window.jQuery || window.$ || null;

const getActiveProjectListTrigger = () => {
  if (activeProjectListTrigger && document.contains(activeProjectListTrigger)) {
    return activeProjectListTrigger;
  }

  activeProjectListTrigger = null;

  return null;
};

const shouldUseCustomProjectListFlow = (element) =>
  Boolean(element?.closest?.(RECENT_PROJECT_LIST_ROOT_SELECTOR));

const stopProjectListEvent = (event) => {
  event.preventDefault();
  event.stopPropagation();

  if (typeof event.stopImmediatePropagation === "function") {
    event.stopImmediatePropagation();
  }
};

const isProjectListLoginRequired = (element) =>
  element.classList.contains("wlfmc-multi-list-btn-login-need") ||
  element.classList.contains("wlfmc-btn-login-need");

const isProjectListPopupVisible = ($, popupId) =>
  $(`#${popupId}`).is(":visible") ||
  $(`#${popupId}_wrapper`).is(":visible") ||
  $(`#${popupId}_background`).is(":visible");

const showProjectListError = (message) => {
  if (!message) {
    return;
  }

  if (window.toastr?.error) {
    window.toastr.error(message);
    return;
  }

  window.alert(message);
};

const clearProjectListFallbackState = () => {
  const $ = getProjectListJQuery();

  prevProjectListState = [];
  activeProjectListTrigger = null;

  if (!$) {
    return;
  }

  $(`#${getMultiListPopupId()}`).removeAttr("data-chamfr-project-list-fallback");
};

const handleProjectListCheckboxChange = (event) => {
  const $ = getProjectListJQuery();

  if (!$) {
    return;
  }

  const $popup = $(event.target).closest(`#${getMultiListPopupId()}`);
  const $submitButton = $popup.find(".wlfmc_add_to_multi_list");
  const checkboxState = $popup
    .find('.list input[type="checkbox"]')
    .toArray()
    .some((checkbox, index) => $(checkbox).prop("checked") !== prevProjectListState[index]);

  $submitButton.prop("disabled", !checkboxState);
};

const loadProjectListPopupContent = ($popup, $button) => {
  const $ = getProjectListJQuery();
  const localization = window.wlfmc_l10n;

  if (!$ || !localization?.actions?.load_lists_action) {
    return;
  }

  const $popupAction = $popup.find(".wlfmc_add_to_multi_list");
  const requestData = {
    action: localization.actions.load_lists_action,
    context: "frontend",
    product_id: $button.attr("data-product-id"),
    exclude_default: $button.attr("data-exclude-default"),
  };
  const cartItemKey = $button.attr("data-cart_item_key");
  const saveCart = $button.attr("data-save-cart");

  if (typeof cartItemKey !== "undefined") {
    requestData.cart_item_key = cartItemKey;
    $popupAction.attr("data-cart_item_key", cartItemKey);
  }

  if (typeof saveCart !== "undefined") {
    $popupAction.attr("data-save-cart", saveCart);
  }

  $popupAction
    .attr("data-product-id", $button.attr("data-product-id"))
    .attr("data-product-type", $button.attr("data-product-type"))
    .attr("data-parent-product-id", $button.attr("data-parent-product-id"));

  $.ajax({
    url: localization.multi_list_ajax_url,
    data: requestData,
    type: "POST",
    beforeSend(xhr) {
      if (localization.ajax_mode === "rest_api") {
        xhr.setRequestHeader("X-WP-Nonce", localization.nonce);
      }

      $.fn.WLFMC?.block?.($popup.find(".wlfmc-add-to-list-container"));
    },
    complete() {
      $.fn.WLFMC?.unblock?.($popup.find(".wlfmc-add-to-list-container"));
    },
    success(response) {
      const template = window.wp?.template?.("wlfmc-list-item");
      const popupLists = Array.isArray(response?.product_lists) ? response.product_lists : [];
      const hasTemplate = typeof template === "function";
      const hasLists =
        response?.result === "true" &&
        Array.isArray(response?.data) &&
        response.data.length > 0 &&
        hasTemplate;

      if (hasLists) {
        const html = response.data.map((item) => template(item)).join("");

        $popup.find(".wlfmc-search-lists").show();
        $popup.find(".list").html(html);
        $popup.find(".wlfmc-number").text(popupLists.length);
        $popup.find(".list-empty").hide();
        $popupAction.attr("data-list-ids", JSON.stringify(popupLists)).show().prop("disabled", true);
        $popup.find(".wlfmc-manage-btn").show();

        prevProjectListState = $popup
          .find('input[type="checkbox"]')
          .map((_, checkbox) => $(checkbox).prop("checked"))
          .get();

        return;
      }

      $popup.find(".wlfmc-search-lists").hide();
      $popup.find(".list").empty();
      $popup.find(".list-empty").show();
      $popup.find(".wlfmc-number").text(0);
      $popupAction.hide().prop("disabled", true);
      $popup.find(".wlfmc-manage-btn").hide();
      prevProjectListState = [];

      if (response?.result !== "true") {
        showProjectListError(response?.message);
      }
    },
  });
};

const openProjectListPopupFallback = (button) => {
  const $ = getProjectListJQuery();
  const popupId = button.getAttribute("data-popup-id") || getMultiListPopupId();

  if (!$ || !popupId) {
    return;
  }

  const $button = $(button);
  const $popup = $(`#${popupId}`);

  if (!$popup.length || typeof $popup.popup !== "function") {
    return;
  }

  if ($.fn.WLFMC?.is_cookie_enabled && !$.fn.WLFMC.is_cookie_enabled()) {
    window.alert(window.wlfmc_l10n?.labels?.cookie_disabled || "");
    return;
  }

  clearProjectListFallbackState();
  activeProjectListTrigger = button;

  if (!$(`#${popupId}_wrapper`).length) {
    $popup.popup({
      absolute: false,
      color: "#333",
      transition: "all 0.3s",
      horizontal: $popup.data("horizontal"),
      vertical: $popup.data("vertical"),
    });
  }

  $("#wlfmc-tooltip")
    .css({ top: "0", left: "0" })
    .removeClass()
    .addClass("tooltip__hidden");

  $popup.attr("data-chamfr-project-list-fallback", "1");
  $popup.popup("show");
  loadProjectListPopupContent($popup, $button);
};

const handleProjectListButtonClick = (event) => {
  const button = event.target.closest(".chamfr-project-list-button");

  if (!button) {
    return;
  }

  if (!shouldUseCustomProjectListFlow(button)) {
    if (getActiveProjectListTrigger()) {
      clearProjectListFallbackState();
    }

    return;
  }

  if (isProjectListLoginRequired(button)) {
    clearProjectListFallbackState();
    return;
  }

  stopProjectListEvent(event);

  const $ = getProjectListJQuery();
  const popupId = button.getAttribute("data-popup-id") || getMultiListPopupId();

  if (!$ || !popupId) {
    return;
  }

  if (getActiveProjectListTrigger() && !isProjectListPopupVisible($, popupId)) {
    clearProjectListFallbackState();
  }

  openProjectListPopupFallback(button);
};

const handleProjectListPopupInteraction = (event) => {
  const $ = getProjectListJQuery();
  const popupId = getMultiListPopupId();
  const activeTrigger = getActiveProjectListTrigger();

  if (!$ || !activeTrigger) {
    return;
  }

  const $popup = $(`#${popupId}`);

  if (!$popup.is('[data-chamfr-project-list-fallback="1"]')) {
    return;
  }

  window.setTimeout(() => {
    if (!isProjectListPopupVisible($, popupId)) {
      clearProjectListFallbackState();
    }
  }, 0);
};

const handleProjectListPopupKeydown = (event) => {
  const $ = getProjectListJQuery();

  if (
    !getActiveProjectListTrigger() ||
    !$ ||
    (event.key !== "Escape" && event.keyCode !== 27)
  ) {
    return;
  }

  window.setTimeout(() => {
    if (!isProjectListPopupVisible($, getMultiListPopupId())) {
      clearProjectListFallbackState();
    }
  }, 0);
};

const bindProjectListFallback = () => {
  if (isProjectListFallbackBound) {
    return;
  }

  document.addEventListener("click", handleProjectListButtonClick, true);
  document.addEventListener("click", handleProjectListPopupInteraction, true);
  document.addEventListener("keydown", handleProjectListPopupKeydown, true);

  const $ = getProjectListJQuery();

  if ($) {
    $(document).on(
      "change.chamfrProjectList",
      `#${getMultiListPopupId()}[data-chamfr-project-list-fallback="1"] .list input[type="checkbox"]`,
      handleProjectListCheckboxChange
    );
    $(document).on(
      "click.chamfrProjectList",
      `#${getMultiListPopupId()} .wlfmc-popup-close`,
      clearProjectListFallbackState
    );
  }

  isProjectListFallbackBound = true;
};

const renderMultiListButton = (hit) => {
  const settings = getMultiListSettings();

  if (!settings.enabled) {
    return "";
  }

  if (!hit.stock_status && !settings.allow_outofstock) {
    return "";
  }

  const buttonClasses = escapeHtml(
    String(settings.button_classes || "alt wlfmc-popup-trigger").trim()
  );
  const productType = escapeHtml(hit.product_type || settings.product_type || "simple");
  const tooltipText = escapeHtml(settings.label || "Add to Project List");
  const parentProductId = escapeHtml(hit.parent_product_id || hit.objectID);

  return `
    <div class="chamfr-project-list-action wlfmc-add-to-multi-list" data-enable-outofstock="${settings.allow_outofstock ? "1" : "0"}">
      <div class="wlfmc-add-button wlfmc-addtomultilist multi-list-tooltip-wrapper">
        <button
          type="button"
          class="${buttonClasses} chamfr-project-list-button"
          data-popup-id="${escapeHtml(getMultiListPopupId())}"
          data-exclude-default="true"
          data-product-id="${hit.objectID}"
          data-product-type="${productType}"
          data-parent-product-id="${parentProductId}">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="27" viewBox="0 0 24 27" fill="none">
            <path d="M2 9.5L2 22C2 24.2091 3.79086 26 6 26L19 26C21.2091 26 23 24.2091 23 22L23 6C23 3.79086 21.2091 2 19 2L10 2" stroke="#8A8A8A" stroke-width="2" stroke-linejoin="round"/>
            <path d="M18 22C18.5523 22 19 21.5523 19 21C19 20.4477 18.5523 20 18 20V22ZM6 21V22H18V21V20H6V21Z" fill="#8A8A8A"/>
            <path d="M18 14C18.5523 14 19 13.5523 19 13C19 12.4477 18.5523 12 18 12V14ZM6 13V14H18V13V12H6V13Z" fill="#8A8A8A"/>
            <path d="M1 5H9" stroke="#8A8A8A" stroke-width="2" stroke-linecap="round"/>
            <path d="M5 1L5 9" stroke="#8A8A8A" stroke-width="2" stroke-linecap="round"/>
            <path d="M18 18C18.5523 18 19 17.5523 19 17C19 16.4477 18.5523 16 18 16V18ZM6 17V18H18V17V16H6V17Z" fill="#8A8A8A"/>
          </svg>
          <span class="multi-list-tooltip">${tooltipText}</span>
        </button>
      </div>
    </div>
  `;
};

const renderHits = (renderOptions, isFirstRender) => {
  const { hits, widgetParams, results, isSearchStalled } = renderOptions;
  let { container, columns = [] } = widgetParams;

  // Subscribe to column state changes on first render
  if (isFirstRender) {
    bindProjectListFallback();

    unsubscribeColumnState = columnState.subscribe((newColumns) => {
      if (newColumns.length > 0) {
        // Update widget params with new columns
        widgetParams.columns = newColumns;
        // Re-render with new columns
        const updatedRenderOptions = {
          ...renderOptions,
          widgetParams: { ...widgetParams, columns: newColumns },
        };
        renderHitsContent(updatedRenderOptions);
      }
    });
  }

  // Always check columnState - it may have been updated by middleware
  const stateColumns = columnState.getColumns();
  if (stateColumns.length > 0) {
    columns = stateColumns;
    widgetParams.columns = stateColumns;
  }

  renderHitsContent({ ...renderOptions, widgetParams: { ...widgetParams, columns } });
};

function renderHitsContent(renderOptions) {
  const { hits, widgetParams, results, isSearchStalled } = renderOptions;
  const { container, columns } = widgetParams;

  const table = container.closest("table.products");
  if (isSearchStalled || !results) {
    if (table) {
      table.classList.add("hidden");
    }
    return;
  } else {
    if (table) {
      table.classList.remove("hidden");
    }
  }

  container.innerHTML = hits
    .map((hit) => {
      const minQty = hit.tier_price_rules?.minimum || 1;

      const volumePricing = hit.tier_price_rules
        ? `
        <div class="volume-pricing-wrapper">
          <span class="volume-pricing-label">Volume Pricing</span>
          <div class="volume-pricing-modal">
            <div class="volume-pricing-table">
              <table width="100%">
                <tbody>
                  ${hit.tier_price_rules.rules
                    .map(
                      (rule) => `
                    <tr>
                      <td class="volume-quantity">
                        ${rule.quantity[0]}${
                        rule.quantity[1] ? "-" + rule.quantity[1] : "+"
                      }
                      </td>
                      <td class="volume-price">
                        ${new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                        }).format(rule.current_price)}
                      </td>
                    </tr>
                  `
                    )
                    .join("")}
                </tbody>
              </table>
            </div>
          </div>
        </div>`
        : "";

      const cartActions = hit.stock_status
        ? `
          <td class="actions">
            <div class="download-add-basket">
              <!--<a href="#" class="download-icon"></a>-->
              <form
                class="cart"
                action="${escapeHtml(hit.url || "")}"
                method="post"
                data-product_id="${escapeHtml(hit.objectID)}"
                enctype="multipart/form-data"
              >
                <div class="quantity">
                  <input
                    type="number"
                    class="input-text qty text"
                    step="1"
                    min="${minQty}"
                    max="${hit.stock_quantity}"
                    name="quantity"
                    value="${minQty}"
                    title="Qty"
                    size="4"
                    placeholder=""
                    inputmode="numeric"
                  />
                </div>
                <button
                  type="submit"
                  name="add-to-cart"
                  value="${hit.objectID}"
                  class="single_add_to_cart_button button"
                >
                  Add
                </button>
              </form>
            </div>
            <p class="ship-within">${hit.taxonomies["pa_lead-time"] || ""}</p>
          </td>`
        : `
          <td class="actions">
            <div class="download-add-basket">
              <!--<a href="#" class="download-icon"></a>-->
              <div class="cart">
                <a
                  href="#"
                  id="back-in-stock-${hit.objectID}"
                  data-product="${hit.objectID}"
                  class="btn btn-outlined btn-disabled back-in-stock-btn"
                >
                  Notify me
                </a>
              </div>
            </div>
            <p class="back-in-stock-label">When it's back in stock</p>
          </td>`;

      const cells = columns
        .map((column) => {
          const label = column.label_column || column.label;
          switch (column.key) {
            case "item-number":
              return `
            <td class="item-number" data-label="${label}">
              <a href="${hit.url}">${hit.item_number || "n/a"}</a>
              ${renderMultiListButton(hit)}
            </td>`;
            case "main-category":
              return `
            <td class="category" data-label="${label}">
              <a href="${hit.main_category_url || "#"}">
                ${hit.main_category || "n/a"}
              </a>
            </td>`;
            case "price":
              return `
            <td class="price" data-label="${label}">
              <p>
                ${new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(hit.price)}
              </p>
              ${volumePricing}
            </td>`;
            default:
              const content = column.attribute.startsWith("taxonomies.")
                ? hit.taxonomies[column.attribute.replace(/^taxonomies\./, "")]
                : hit[column.attribute];

              const value = column.formatValue
                ? column.formatValue(content)
                : content;

              return `
            <td class="${column.key}" data-label="${label}">
              ${(value || "n/a").toString().replace(/\//g, "/\u200B")}
            </td>`;
          }
        })
        .join("");

      return `
      <tr class="product-row-${hit.objectID}" data-object-id="${hit.objectID}" data-position="${hit.__position}" data-query-id="${results.queryID}">
        ${cells}
		<td class="item-description" data-label="Description">${hit.name}</td>
        ${cartActions}
      </tr>`;
    })
    .join("");

  container.querySelectorAll(".item-number a").forEach((link) => {
    link.addEventListener("click", (e) => {
      e.preventDefault();
      const row = link.closest("tr");
      const hit = {
        objectID: row.getAttribute("data-object-id"),
        __position: parseInt(row.getAttribute("data-position"), 10),
        __queryID: row.getAttribute("data-query-id"),
        url: link.href,
      };
      trackItemClick(hit);
      setTimeout(() => {
        window.location.href = link.href;
      }, 50);
    });
  });

  container.querySelectorAll("form.cart").forEach((form) => {
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const row = form.closest("tr");
      const hit = {
        objectID: row.getAttribute("data-object-id"),
        __position: parseInt(row.getAttribute("data-position"), 10),
        __queryID: row.getAttribute("data-query-id"),
      };
      trackConversion(hit);
    });
  });
}

export const chamfrProductsHits = connectHits(renderHits);
